import _ from "lodash";
import { useContext, useMemo, useState } from "react";
import { Alert, Col, Nav, Row, Tab } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import Loading from "../../Loading";
import TaxPerformancePeriodSelector from "../../components/TaxPerformancePeriodSelector";
import { formatCurrencyComponent, formatPercent } from "../../lib/numbers";
import { resolveAccountTaxType } from "../../lib/tax-registration";
import { HouseholdContext } from "./HouseholdInfo";
import HouseholdRMD from "./HouseholdRMD";
import HouseholdTaxTable from "./HouseholdTaxTable";
import { useQueryHouseholdPerformance } from "./lib";

// Query-param key for the Tax sub-tab so reloads preserve the selected tab.
const TAB_PARAM = "tab";
const TAB_RMD = "rmd";
const TAB_SUMMARY = "summary";
const tabKeyFromParam = (value: string | null): string =>
  value === TAB_RMD ? "rmd-tracking" : "tax-summary";
const paramFromTabKey = (key: string): string =>
  key === "rmd-tracking" ? TAB_RMD : TAB_SUMMARY;

const HouseholdTax = () => {
  const { household } = useContext(HouseholdContext);
  const { householdId } = useParams();
  const [customTaxPeriodParams, setCustomTaxPeriodParams] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = tabKeyFromParam(searchParams.get(TAB_PARAM));
  const handleTabSelect = (key: string | null) => {
    if (!key) return;
    const next = new URLSearchParams(searchParams);
    next.set(TAB_PARAM, paramFromTabKey(key));
    setSearchParams(next, { replace: true });
  };

  // Every page of `GET /households/:id/performance` (APP-1653).
  const {
    data: dataHouseholdAccountTaxPerformance,
    isPending: isPendingHouseholdAccountTaxPerformance,
    isError: isErrorHouseholdAccountTaxPerformance,
  } = useQueryHouseholdPerformance(
    householdId === undefined ? undefined : parseInt(householdId),
    customTaxPeriodParams,
  );

  // An account is taxable when `resolveAccountTaxType` says so, as for the
  // label that `HouseholdTaxTable` shows, and not from the BridgeFT flag
  // `isTaxable` alone (APP-1734).
  const taxableAccounts = useMemo(
    () =>
      (household?.accounts ?? []).filter(
        (account) => resolveAccountTaxType(account).isTaxable,
      ),
    [household?.accounts],
  );

  // holds the total income and balance for all accounts
  const householdTotals = useMemo(
    () =>
      (dataHouseholdAccountTaxPerformance ?? []).reduce(
        (sums, account) => ({
          balance: sums.balance + account.balance,
          income: sums.income + account.income,
        }),
        {
          balance: 0,
          income: 0,
        },
      ),
    [dataHouseholdAccountTaxPerformance],
  );

  const taxTotals = useMemo(() => {
    const accountsMap = _.keyBy(taxableAccounts, "id");
    const householdAccountsMap = _.keyBy(household?.accounts ?? [], "id");
    // holds the total income and balance for all taxable accounts. A
    // performance entry whose account is not in `household.accounts` has no
    // inputs for `resolveAccountTaxType`, so it keeps its own `isTaxable`.
    const householdAccountTaxPerformanceData = (
      dataHouseholdAccountTaxPerformance ?? []
    ).filter((account) =>
      account.accountId !== undefined &&
      householdAccountsMap[account.accountId] !== undefined
        ? accountsMap[account.accountId] !== undefined
        : account.isTaxable,
    );

    return householdAccountTaxPerformanceData.reduce(
      (sums, account) => {
        const accountWithMetrics = accountsMap[account.accountId!];

        return {
          balance: sums.balance + account.balance,
          realizedGainLoss: sums.realizedGainLoss + account.realizedGainLoss,
          realizedGainLossST:
            sums.realizedGainLossST + account.realizedGainLossST,
          realizedGainLossLT:
            sums.realizedGainLossLT + account.realizedGainLossLT,
          income: sums.income + account.income,
          unrealizedGainLoss:
            sums.unrealizedGainLoss + account.unrealizedGainLoss,
          unrealizedLoss:
            sums.unrealizedLoss + (accountWithMetrics?.unrealizedLoss ?? 0),
        };
      },
      {
        balance: 0,
        realizedGainLoss: 0,
        realizedGainLossST: 0,
        realizedGainLossLT: 0,
        income: 0,
        unrealizedGainLoss: 0,
        unrealizedLoss: 0,
      },
    );
  }, [
    dataHouseholdAccountTaxPerformance,
    household?.accounts,
    taxableAccounts,
  ]);

  const oldestAccount = _.minBy(household?.accounts ?? [], "inceptionDate");

  return (
    <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
      <Nav variant="pills" className="mb-3">
        <Nav.Item>
          <Nav.Link eventKey="tax-summary">Tax Summary</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="rmd-tracking">RMD Tracking</Nav.Link>
        </Nav.Item>
      </Nav>

      <Tab.Content>
        <Tab.Pane eventKey="tax-summary">
          <Row className="mb-2">
            <Col xl={2} md={2}>
              <TaxPerformancePeriodSelector
                inceptionDate={oldestAccount?.inceptionDate}
                setCustomPeriodParams={setCustomTaxPeriodParams}
              />
            </Col>
            {isPendingHouseholdAccountTaxPerformance ? (
              <Loading message="Tax Details" />
            ) : isErrorHouseholdAccountTaxPerformance ? (
              <Alert variant="danger">Failed to load tax details</Alert>
            ) : (
              <>
                <Col md={4}>
                  <Row>
                    <Col as="dt" md={7} sm={8} xs={8}>
                      Total Div./Int.
                    </Col>
                    <Col as="dd">
                      {formatCurrencyComponent(householdTotals.income, 2)} (
                      {formatPercent(
                        householdTotals.balance === 0
                          ? 0
                          : householdTotals.income / householdTotals.balance,
                        2,
                      )}
                      )
                    </Col>
                  </Row>
                  <Row>
                    <Col as="dt" md={7} sm={8} xs={8}>
                      Total Taxable Div./Int.
                    </Col>
                    <Col as="dd" md={5} sm={4} xs={4}>
                      {formatCurrencyComponent(taxTotals.income, 2)} (
                      {formatPercent(
                        taxTotals.balance === 0
                          ? 0
                          : taxTotals.income / taxTotals.balance,
                        2,
                      )}
                      )
                    </Col>
                  </Row>
                  <Row>
                    <Col as="dt" md={7} sm={8} xs={8}>
                      TLH Opportunities
                    </Col>
                    <Col as="dd" md={5} sm={4} xs={4}>
                      {formatCurrencyComponent(taxTotals.unrealizedLoss, 2)} (
                      {formatPercent(
                        taxTotals.balance === 0
                          ? 0
                          : taxTotals.unrealizedLoss / taxTotals.balance,
                        2,
                      )}
                      )
                    </Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row>
                    <Col as="dt" md={8} sm={8} xs={8}>
                      Taxable Realized LT G/L
                    </Col>
                    <Col as="dd">
                      {formatCurrencyComponent(taxTotals.realizedGainLossLT, 2)}
                    </Col>
                  </Row>
                  <Row>
                    <Col as="dt" sm={8} xs={8}>
                      Taxable Realized ST G/L
                    </Col>
                    <Col as="dd">
                      {formatCurrencyComponent(taxTotals.realizedGainLossST, 2)}
                    </Col>
                  </Row>
                  <Row>
                    <Col as="dt" sm={8} xs={8}>
                      Total Taxable Realized G/L
                    </Col>
                    <Col as="dd">
                      {formatCurrencyComponent(
                        taxTotals.realizedGainLossLT +
                          taxTotals.realizedGainLossST,
                        2,
                      )}
                    </Col>
                  </Row>
                </Col>
              </>
            )}
          </Row>
          <Row>
            <Col>
              <h3>Account Holdings</h3>
              <HouseholdTaxTable
                accounts={taxableAccounts}
                customTaxPeriodParams={customTaxPeriodParams}
              />
            </Col>
          </Row>
        </Tab.Pane>

        <Tab.Pane eventKey="rmd-tracking">
          <HouseholdRMD />
        </Tab.Pane>
      </Tab.Content>
    </Tab.Container>
  );
};

export default HouseholdTax;
